// El panel de herramientas de edición, a la derecha del visor de escritorio —
// ADR-2026-10-06-panel-de-edicion-en-el-visor.
//
// Mientras se edita en el visor (ADR-2026-10-06-editar-en-el-visor), un panel de vidrio a la
// derecha lista tres herramientas: Recortar, Expandir y Mejorar. Sin ninguna elegida, la
// edición es la de siempre: el prompt de abajo (ADR-2026-10-06-describir-es-el-estado-por-defecto).
// Tocar una cambia el panel a su menú, con un chevron para volver a la lista al lado del
// título; la imagen no se mueve:
//
//  • Recortar dibuja el marco sobre la MISMA imagen (una capa calzada en su lugar) y guarda
//    el recorte como versión nueva (`POST /v1/images/crop`, gratis), o lo descarga.
//  • Expandir: la vista previa del encuadre nuevo se dibuja SOBRE la imagen grande; la
//    expansión es versión nueva.
//  • Mejorar escribe la versión mejorada en la MISMA imagen (como siempre) y el visor la
//    suma a la columna como versión aparte.
//
// Los menús son los de siempre (`UpscaleMenu`, `ExpansionMagicaSheet`, `ImageCropControls`)
// adaptados al vidrio oscuro del visor: título, secciones agrupadas y la acción abajo. La
// lógica de cada uno (guard de saldo, llamada, límites de escala) es la misma.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Crop, Download, Expand, Info, Layers, Maximize, Package, RotateCcw, Search, UserRound } from 'lucide-react';
import { ASPECT_OPTIONS, ImageCropCanvas, useCropState } from '../ImageCropModal';
import { BotonConCreditos } from './BotonConCreditos';
import { Cuerpo, Pie, Seccion, useCalceSobre } from './piezasDelPanel';
import { ModoCapas } from './ModoCapas';
import { componerCapas, CREDITOS_EDITAR_CAPA, CREDITOS_SEPARAR_EN_CAPAS, editarCapa, guardarCapas, separarEnCapas, type CapaEnLienzo, type Separacion } from '../../lib/separarEnCapas';
import { buscarFila, type FilaEditada } from '../BarraDelVisor';
import {
  CREDITOS_EXPANSION,
  expandirImagen,
  ladoQueCrece,
  opcionesParaImagen,
  RATIOS_EXPANSION,
  type RatioExpansion,
} from '../../lib/expansionMagica';
import { GenerateAdError } from '../../lib/generateAdRequest';
import { MODO_UTILIZADO_EDICION, mensajeDeUpscaleFallido, postImageEdit, postImageUpscale, type ImageEditResponse } from '../../lib/imageEdit';
import { USE_ATLAS_BACKEND } from '../../config';
import { usePalabraDeModelo } from '../../lib/nombreDeModelo';
import { useRubroMarcaActiva } from '../../hooks/useRubroMarca';
import { firmarMiniaturaDeProducto, listarProductosParaElegir, type ProductoParaElegir } from '../../lib/productosParaElegir';
import { firmarFotoDeModelo, type ModelV2 } from '../../pages/v2/modelsApi';
import { fotoDe, useModelos } from '../../pages/flujos/panels/catalogoPersonas';
import { rubroDelPrompt } from '../../pages/v2/replicarFoto';
import CachedImg from '../CachedImg';
import { supabase } from '../../lib/supabase';
import { mayorEscalaQueEntra, upscaleEntraEnStorage, type Dimensiones } from '../../lib/upscaleLimits';
import { activarVersion, formatoDeMedidas, guardarRecorte } from '../../lib/versionesDelVisor';
import { setCachedSignedUrl } from '../../utils/imageCache';
import { consultarSaldo, tipoDeLimite, type TipoDeLimite } from '../../chat-atlas/lienzo/herramientas/saldo';
import { sonarListo } from '../../utils/sonarListo';

/** Sin herramienta elegida (`null`) es la edición de siempre: el prompt de abajo. */
export type HerramientaDeEdicion = 'recortar' | 'expandir' | 'mejorar' | 'producto' | 'modelo' | 'capas';

const HERRAMIENTAS: { id: HerramientaDeEdicion; nombre: string; bajada: string; Icono: typeof Crop }[] = [
  { id: 'recortar', nombre: 'Recortar', bajada: 'Encuadrá y guardá una versión', Icono: Crop },
  { id: 'expandir', nombre: 'Expandir', bajada: 'Llevá la imagen a otro formato', Icono: Expand },
  { id: 'mejorar', nombre: 'Mejorar', bajada: 'Subí la resolución con IA', Icono: Maximize },
  // ADR-2026-10-06-cambiar-producto-y-modelo-en-el-visor. El nombre de "modelo" sigue al
  // rubro (Modelo / Avatar): se completa al dibujar la lista.
  { id: 'producto', nombre: 'Cambiar producto', bajada: 'Poné otro de tus productos', Icono: Package },
  { id: 'modelo', nombre: 'Cambiar modelo', bajada: 'Otra persona, la misma foto', Icono: UserRound },
  // ADR-2026-10-07-separar-en-capas-en-el-visor.
  { id: 'capas', nombre: 'Separar en capas', bajada: 'Mové, ordená u ocultá cada elemento', Icono: Layers },
];

export interface ImagenEnEdicion {
  id: string;
  fileName: string;
  /** La URL que muestra el visor. */
  srcVisible: string;
  /** La firmada del `full/` (recorte y medidas). */
  srcOriginal: string;
  /** Ya tiene una versión mejorada. */
  yaMejorada: boolean;
}

// ── Recortar ────────────────────────────────────────────────────────────────────

/** Aire alrededor de la imagen en el lienzo del recorte, en px. */
const MARGEN_RECORTE = 24;

const ModoRecortar: React.FC<{
  imagen: ImagenEnEdicion;
  imgRef: RefObject<HTMLImageElement>;
  ocupado: boolean;
  onGuardar: (area: { x: number; y: number; width: number; height: number }, formato?: string) => void;
}> = ({ imagen, imgRef, ocupado, onGuardar }) => {
  // El lienzo del recorte se agranda MARGEN_RECORTE de cada lado de la imagen y la imagen
  // entra en el medio, en el mismo lugar que la del visor: así se agarran los bordes y las
  // esquinas cuando el recorte llega al límite (ADR-2026-10-07-el-recorte-se-agarra-facil).
  const recorte = useCropState(imagen.srcOriginal, imagen.fileName, () => {}, { margen: MARGEN_RECORTE });
  const calce = useCalceSobre(imgRef, true);
  const rect = calce
    ? {
        left: calce.left - MARGEN_RECORTE,
        top: calce.top - MARGEN_RECORTE,
        width: calce.width + 2 * MARGEN_RECORTE,
        height: calce.height + 2 * MARGEN_RECORTE,
      }
    : null;
  const area = recorte.areaNormalizada;
  const esEntera = !area || (area.x < 0.001 && area.y < 0.001 && area.width > 0.999 && area.height > 0.999);
  const opcion = ASPECT_OPTIONS[recorte.selectedAspect];
  const formato =
    opcion?.ratio && opcion.label.includes(':') ? opcion.label : formatoDeMedidas(recorte.realWidth, recorte.realHeight);
  return (
    <>
      {/* El marco, sobre la misma imagen: mismo lugar, mismo tamaño. Por portal porque el
          panel tiene `backdrop-filter` y una capa fija adentro quedaría atada a él. */}
      {rect &&
        createPortal(
          <div className="fixed z-[55] animate-fade-in" style={rect}>
            <ImageCropCanvas cropState={recorte} fondoClassName="bg-transparent" />
          </div>,
          document.body,
        )}
      <Cuerpo>
        <Seccion titulo="Proporción">
          <div className="grid grid-cols-3 gap-1.5 p-2">
            {ASPECT_OPTIONS.map((o, i) => (
              <button
                key={o.label}
                type="button"
                onClick={() => recorte.handleAspectChange(i)}
                className={`h-9 rounded-lg text-[13.5px] font-medium transition-colors ${
                  recorte.selectedAspect === i ? 'bg-[#1644EB] text-white' : 'bg-white/[0.06] text-white/70 hover:bg-white/[0.12] hover:text-white'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Seccion>
        <Seccion titulo="Tamaño">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-[14px] tabular-nums text-white/85">
              {recorte.imageLoaded ? `${recorte.realWidth} × ${recorte.realHeight} px` : '—'}
            </span>
            <button
              type="button"
              onClick={recorte.handleReset}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] text-white/55 transition-colors hover:bg-white/[0.08] hover:text-white"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Restablecer
            </button>
          </div>
        </Seccion>
        <p className="text-[12.5px] leading-snug text-white/45">
          Arrastrá las esquinas sobre la imagen. Guardarlo crea una versión nueva y no cuesta créditos.
        </p>
      </Cuerpo>
      <Pie>
        <button
          type="button"
          onClick={recorte.handleCropAndDownload}
          disabled={!recorte.imageLoaded || ocupado}
          className="mr-auto inline-flex h-10 items-center gap-1.5 rounded-full px-3 text-[13.5px] font-medium text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          Descargar
        </button>
        <BotonConCreditos ocupado={ocupado} disabled={!recorte.imageLoaded || esEntera} onClick={() => area && onGuardar(area, formato)}>
          {ocupado ? 'Guardando…' : 'Guardar versión'}
        </BotonConCreditos>
      </Pie>
    </>
  );
};

// ── Expandir ────────────────────────────────────────────────────────────────────

const ModoExpandir: React.FC<{
  medidas: Dimensiones | null;
  ocupado: boolean;
  onExpandir: (r: RatioExpansion) => void;
  /** El formato elegido, para dibujar la vista previa sobre la imagen grande. */
  onRatio: (r: RatioExpansion | null) => void;
}> = ({ medidas, ocupado, onExpandir, onRatio }) => {
  const w = medidas?.width ?? 0;
  const h = medidas?.height ?? 0;
  const opciones = useMemo(() => opcionesParaImagen(w, h), [w, h]);
  const [ratio, setRatio] = useState<RatioExpansion | null>(null);
  useEffect(() => {
    if (ratio !== null) return;
    const primera = opciones.find((o) => o.disponible);
    if (primera) setRatio(primera.id);
  }, [opciones, ratio]);
  useEffect(() => {
    onRatio(ratio);
  }, [ratio, onRatio]);
  // Al salir de Expandir se borra la vista previa.
  useEffect(() => () => onRatio(null), [onRatio]);
  const crece = ratio ? ladoQueCrece(ratio, w, h) : null;
  return (
    <>
      <Cuerpo>
        <Seccion titulo="Formato">
          <div className="grid grid-cols-2 gap-1.5 p-2">
            {opciones.map((o) => (
              <button
                key={o.id}
                type="button"
                disabled={!o.disponible || ocupado}
                title={!o.disponible ? 'La imagen ya tiene este formato' : undefined}
                onClick={() => setRatio(o.id)}
                className={`flex h-12 flex-col items-center justify-center rounded-lg transition-colors ${
                  !o.disponible
                    ? 'cursor-not-allowed bg-white/[0.03] text-white/25'
                    : ratio === o.id
                      ? 'bg-[#1644EB] text-white'
                      : 'bg-white/[0.06] text-white/75 hover:bg-white/[0.12] hover:text-white'
                }`}
              >
                <span className="text-[13.5px] font-semibold leading-none">{o.id}</span>
                <span className="mt-1 text-[11px] leading-none opacity-70">{o.etiqueta}</span>
              </button>
            ))}
          </div>
        </Seccion>
        <p className="text-[12.5px] leading-snug text-white/45">
          {crece
            ? `La imagen crece hacia ${crece === 'costados' ? 'los costados' : 'arriba y abajo'}: la IA completa lo nuevo y queda como una versión nueva.`
            : 'Elegí el formato al que querés llevar la imagen.'}{' '}
          Lo ves sobre la imagen.
        </p>
      </Cuerpo>
      <Pie>
        <BotonConCreditos creditos={CREDITOS_EXPANSION} ocupado={ocupado} disabled={!ratio || !medidas} onClick={() => ratio && onExpandir(ratio)}>
          {ocupado ? 'Expandiendo…' : 'Expandir'}
        </BotonConCreditos>
      </Pie>
    </>
  );
};

// ── Mejorar ─────────────────────────────────────────────────────────────────────

const ModoMejorar: React.FC<{
  imagen: ImagenEnEdicion;
  medidas: Dimensiones | null;
  ocupado: boolean;
  onMejorar: (escala: 2 | 4) => void;
}> = ({ imagen, medidas, ocupado, onMejorar }) => {
  const [escala, setEscala] = useState<2 | 4>(2);
  const maxima = medidas ? mayorEscalaQueEntra(medidas) : 4;
  useEffect(() => {
    if (medidas && !upscaleEntraEnStorage(medidas, escala) && maxima) setEscala(maxima);
  }, [medidas, escala, maxima]);
  const costo = escala === 2 ? 1 : 2;
  return (
    <>
      <Cuerpo>
        {imagen.yaMejorada && (
          <div className="flex gap-3 rounded-[13px] border border-amber-300/20 bg-amber-300/10 px-4 py-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" aria-hidden="true" />
            <p className="text-[13px] leading-snug text-amber-100/90">
              Esta imagen ya tiene una versión mejorada: la vas a reemplazar y {costo === 1 ? 'se consume 1 imagen' : `se consumen ${costo} imágenes`} otra vez.
            </p>
          </div>
        )}
        <Seccion titulo="Escala">
          <div className="flex gap-2 p-2">
            {([2, 4] as const).map((e) => {
              const noEntra = medidas ? !upscaleEntraEnStorage(medidas, e) : false;
              return (
                <button
                  key={e}
                  type="button"
                  disabled={noEntra || ocupado}
                  title={noEntra ? 'Esta imagen es demasiado grande para esta escala' : undefined}
                  onClick={() => setEscala(e)}
                  className={`h-10 flex-1 rounded-lg text-[15px] font-medium transition-colors ${
                    noEntra
                      ? 'cursor-not-allowed bg-white/[0.03] text-white/25'
                      : escala === e
                        ? 'bg-[#1644EB] text-white'
                        : 'bg-white/[0.06] text-white/75 hover:bg-white/[0.12] hover:text-white'
                  }`}
                >
                  {e}x
                </button>
              );
            })}
          </div>
          {medidas && maxima === null && (
            <p className="px-4 pb-3 text-[12.5px] text-amber-200/90">Esta imagen es demasiado grande para mejorarla más.</p>
          )}
        </Seccion>
        <Seccion titulo="Información">
          <div className="px-4 py-3">
            <p className="text-[14px] text-white/90">Resolución {escala}x</p>
            <p className="mt-1 text-[12.5px] leading-snug text-white/55">
              La imagen se mejora a {escala} veces su resolución con IA. Queda en la misma imagen y la ves como una versión aparte.
            </p>
          </div>
        </Seccion>
      </Cuerpo>
      <Pie>
        <BotonConCreditos creditos={costo} ocupado={ocupado} disabled={!!medidas && maxima === null} onClick={() => onMejorar(escala)}>
          {ocupado ? 'Mejorando…' : 'Mejorar'}
        </BotonConCreditos>
      </Pie>
    </>
  );
};

/** Lo que ocupan, a cada lado, la columna de versiones y el panel (el mismo margen que el visor
 *  le deja a la imagen editando: `calc(100% − 808px)`). */
const MARGEN_LATERAL_DE_EDICION = 404;
const CURVA = 'cubic-bezier(0.32,0.72,0,1)';

/**
 * La vista previa de la expansión, SOBRE la imagen grande: el marco del formato nuevo,
 * centrado en la imagen, con lo que la IA va a completar borroneado y la original nítida en
 * su lugar exacto con un filo blanco. Cambiar de formato anima el marco; mientras se
 * expande, el fondo late y pasa el brillo. Va por debajo del panel y de la columna de
 * versiones (z 25 < 30).
 *
 * Para que el marco ENTRE en la pantalla, la imagen se achica lo justo (escala sobre su
 * centro, animada) y el marco se calcula con esa misma escala y la misma curva: los dos
 * se mueven juntos. Al salir de Expandir la imagen vuelve a su tamaño
 * (ADR-2026-10-06-la-imagen-se-achica-para-que-entre-la-expansion).
 */
const VistaPreviaDeExpansion: React.FC<{
  imgRef: RefObject<HTMLImageElement>;
  src: string;
  ratio: RatioExpansion;
  ocupado: boolean;
}> = ({ imgRef, src, ratio, ocupado }) => {
  // La imagen SIN escalar: tamaño de layout (`offsetWidth`, que no ve el transform) y su
  // centro (que la escala sobre el centro no mueve). Se remide al cambiar la ventana.
  const [base, setBase] = useState<{ w: number; h: number; cx: number; cy: number } | null>(null);
  useLayoutEffect(() => {
    const medir = () => {
      const img = imgRef.current;
      if (!img || img.offsetWidth === 0) return;
      const r = img.getBoundingClientRect();
      setBase({ w: img.offsetWidth, h: img.offsetHeight, cx: r.left + r.width / 2, cy: r.top + r.height / 2 });
    };
    medir();
    // También al cargar: la imagen puede cambiar de archivo (otra versión) con la vista abierta.
    const img = imgRef.current;
    img?.addEventListener('load', medir);
    window.addEventListener('resize', medir);
    return () => {
      img?.removeEventListener('load', medir);
      window.removeEventListener('resize', medir);
    };
  }, [imgRef]);

  const destino = RATIOS_EXPANSION.find((o) => o.id === ratio);
  const t = destino ? destino.w / destino.h : 1;
  const geo = base
    ? (() => {
        const propio = base.w / base.h;
        const marcoW = t > propio ? base.h * t : base.w;
        const marcoH = t > propio ? base.h : base.w / t;
        const libreW = Math.max(120, window.innerWidth - 2 * MARGEN_LATERAL_DE_EDICION);
        const libreH = window.innerHeight * 0.85;
        const escala = Math.min(1, libreW / marcoW, libreH / marcoH);
        return { escala, marcoW: marcoW * escala, marcoH: marcoH * escala, imgW: base.w * escala, imgH: base.h * escala };
      })()
    : null;

  // La escala va directo al estilo de la imagen (el visor no maneja `transform` en ella).
  const escala = geo?.escala ?? 1;
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    img.style.transform = escala < 1 ? `scale(${escala})` : '';
  }, [imgRef, escala]);
  useEffect(
    () => () => {
      if (imgRef.current) imgRef.current.style.transform = '';
    },
    [imgRef],
  );

  if (!base || !geo) return null;
  const left = base.cx - geo.marcoW / 2;
  const top = base.cy - geo.marcoH / 2;
  const mover = `left 320ms ${CURVA}, top 320ms ${CURVA}, width 320ms ${CURVA}, height 320ms ${CURVA}`;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed z-[25] overflow-hidden rounded-lg animate-fade-in"
      style={{ left, top, width: geo.marcoW, height: geo.marcoH, transition: mover }}
    >
      <img
        src={src}
        alt=""
        className={`absolute inset-0 h-full w-full object-cover blur-2xl ${ocupado ? 'expansion-latido' : 'opacity-75'}`}
        style={{ transform: 'scale(1.25)' }}
      />
      {/* La original, exactamente donde queda la imagen del visor (ya achicada). */}
      <img
        src={src}
        alt=""
        className="absolute object-contain"
        style={{
          left: (geo.marcoW - geo.imgW) / 2,
          top: (geo.marcoH - geo.imgH) / 2,
          width: geo.imgW,
          height: geo.imgH,
          boxShadow: '0 0 0 1px rgba(255,255,255,0.65)',
          transition: mover,
        }}
      />
      {ocupado && <span className="brillo-de-edicion" />}
    </div>
  );
};

// ── Cambiar producto / modelo ───────────────────────────────────────────────────
// ADR-2026-10-06-cambiar-producto-y-modelo-en-el-visor.

/** Cuántas miniaturas se dibujan por tanda: seis filas de dos. */
const TANDA_DE_GRILLA = 12;

/**
 * Grilla de elección (productos o modelos), en dos columnas: miniatura, nombre y la elegida
 * con anillo. Dibuja de a `TANDA_DE_GRILLA` y suma otra tanda cuando el pie de la grilla
 * entra a la vista, como el selector de fuentes del agente
 * (ADR-2026-09-25-el-selector-de-fuentes-carga-miniaturas-y-por-scroll): las miniaturas que
 * no se dibujan no se bajan. Con `buscador`, filtra por nombre (sin distinguir mayúsculas)
 * y vuelve a la primera tanda.
 */
/** Un ítem de la grilla. Sin `imagen`, `firmarImagen` la consigue cuando la celda se dibuja. */
interface ItemDeEleccion {
  id: string;
  nombre: string;
  imagen?: string;
  firmarImagen?: () => Promise<string | null>;
}

/** La miniatura de una celda: la que trae el ítem, o la que firma al montarse. */
const MiniaturaDeEleccion: React.FC<{ item: ItemDeEleccion }> = ({ item }) => {
  const [src, setSrc] = useState<string | null>(item.imagen ?? null);
  useEffect(() => {
    if (item.imagen || !item.firmarImagen) return;
    let vivo = true;
    void item
      .firmarImagen()
      .then((u) => vivo && setSrc(u))
      .catch(() => {});
    return () => {
      vivo = false;
    };
    // El ítem se identifica por id: firmar otra vez por un objeto nuevo sería en vano.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.imagen]);
  return src ? (
    <CachedImg src={src} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
  ) : null;
};

const GrillaDeEleccion: React.FC<{
  items: ItemDeEleccion[] | null;
  elegido: string | null;
  onElegir: (id: string) => void;
  aspecto: string;
  vacio: string;
  disabled?: boolean;
  /** El texto del buscador ("Buscar productos"); sin él, no hay buscador. */
  buscador?: string;
}> = ({ items, elegido, onElegir, aspecto, vacio, disabled, buscador }) => {
  const [busqueda, setBusqueda] = useState('');
  const [dibujadas, setDibujadas] = useState(TANDA_DE_GRILLA);
  useEffect(() => setDibujadas(TANDA_DE_GRILLA), [busqueda]);
  const filtro = busqueda.trim().toLowerCase();
  const visibles = items ? (filtro ? items.filter((it) => it.nombre.toLowerCase().includes(filtro)) : items) : null;
  const quedan = !!visibles && dibujadas < visibles.length;

  const pieRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const pie = pieRef.current;
    if (!pie || !quedan) return;
    const obs = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) setDibujadas((n) => n + TANDA_DE_GRILLA);
      },
      { rootMargin: '0px 0px 200px 0px' },
    );
    obs.observe(pie);
    return () => obs.disconnect();
  }, [quedan, dibujadas]);

  return (
    <div className="space-y-3">
      {buscador && items !== null && items.length > 0 && (
        // El buscador de las bibliotecas (redondeado, con la lupa), en el vidrio del panel.
        <label className="flex items-center gap-2 rounded-xl bg-white/[0.08] px-3 py-2 ring-white/20 focus-within:ring-1">
          <Search className="h-4 w-4 shrink-0 text-white/45" aria-hidden="true" />
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder={buscador}
            aria-label={buscador}
            className="min-w-0 flex-1 bg-transparent text-[13.5px] text-white outline-none placeholder:text-white/40"
          />
        </label>
      )}
      {visibles === null ? (
        <div className="grid grid-cols-2 gap-2.5" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="block animate-pulse rounded-lg bg-white/[0.06]" style={{ aspectRatio: aspecto }} />
          ))}
        </div>
      ) : visibles.length === 0 ? (
        <p className="text-[13px] leading-snug text-white/55">{filtro ? `Nada coincide con "${busqueda.trim()}".` : vacio}</p>
      ) : (
        <>
          <div role="listbox" className="grid grid-cols-2 gap-2.5">
            {visibles.slice(0, dibujadas).map((it) => {
              const activo = it.id === elegido;
              return (
                <button
                  key={it.id}
                  type="button"
                  role="option"
                  aria-selected={activo}
                  disabled={disabled}
                  onClick={() => onElegir(it.id)}
                  className="group version-entra flex min-w-0 flex-col gap-1.5 text-left disabled:opacity-50"
                >
                  <span
                    className={`relative block w-full overflow-hidden rounded-lg bg-white/[0.06] ring-2 transition-[box-shadow,opacity] duration-200 ${
                      activo ? 'ring-white' : 'ring-transparent opacity-85 group-hover:opacity-100'
                    }`}
                    style={{ aspectRatio: aspecto }}
                  >
                    <MiniaturaDeEleccion item={it} />
                  </span>
                  <span className={`truncate text-[12px] font-medium ${activo ? 'text-white' : 'text-white/60'}`}>{it.nombre}</span>
                </button>
              );
            })}
          </div>
          {/* El centinela: a la vista, se dibuja la próxima tanda. */}
          {quedan && <div ref={pieRef} aria-hidden="true" className="h-px" />}
        </>
      )}
    </div>
  );
};

const ModoCambiarProducto: React.FC<{
  companyId: string | null;
  ocupado: boolean;
  onCambiar: (varianteId: string) => void;
}> = ({ companyId, ocupado, onCambiar }) => {
  // Los productos de la marca activa y los que no tienen marca, livianos: la lista sin
  // firmar, y cada miniatura se firma cuando su tanda se dibuja
  // (ADR-2026-10-06-elegir-producto-por-tandas). `marcaId` sólo dispara la recarga al
  // cambiar de marca; la consulta resuelve la marca activa ella misma.
  const { marcaId, loaded } = useRubroMarcaActiva();
  const [productos, setProductos] = useState<ProductoParaElegir[] | null>(null);
  const [elegido, setElegido] = useState<string | null>(null);
  useEffect(() => {
    if (!companyId || !loaded) return;
    let vivo = true;
    void listarProductosParaElegir(companyId)
      .then((p) => vivo && setProductos(p))
      .catch(() => vivo && setProductos([]));
    return () => {
      vivo = false;
    };
  }, [companyId, marcaId, loaded]);
  const items = useMemo(
    () =>
      productos?.map((p) => ({
        id: p.id,
        nombre: p.nombre,
        firmarImagen: companyId ? () => firmarMiniaturaDeProducto(companyId, p) : undefined,
      })) ?? null,
    [productos, companyId],
  );
  return (
    <>
      <Cuerpo>
        {/* Debajo del título: al pie de una lista larga no se veía. */}
        <p className="text-[12.5px] leading-snug text-white/55">
          El producto de la foto se reemplaza por el que elijas; la escena y la persona quedan igual.
        </p>
        <Seccion titulo="Tus productos">
          <div className="p-2">
            <GrillaDeEleccion
              items={items}
              elegido={elegido}
              onElegir={setElegido}
              aspecto="1 / 1"
              vacio="Todavía no cargaste productos en esta marca."
              disabled={ocupado}
              buscador="Buscar productos"
            />
          </div>
        </Seccion>
      </Cuerpo>
      <Pie>
        <BotonConCreditos creditos={1} ocupado={ocupado} disabled={!elegido} onClick={() => elegido && onCambiar(elegido)}>
          {ocupado ? 'Cambiando…' : 'Cambiar producto'}
        </BotonConCreditos>
      </Pie>
    </>
  );
};

const ModoCambiarModelo: React.FC<{
  companyId: string | null;
  ocupado: boolean;
  onCambiar: (modelo: ModelV2) => void;
}> = ({ companyId, ocupado, onCambiar }) => {
  const palabra = usePalabraDeModelo();
  // Refrescando: un modelo creado durante la sesión tiene que aparecer acá.
  const modelos = useModelos(companyId, { refrescar: true });
  const [elegido, setElegido] = useState<string | null>(null);
  const conFoto = modelos?.filter((m) => !!m.fotoFileName) ?? null;
  const modelo = conFoto?.find((m) => String(m.id) === elegido) ?? null;
  return (
    <>
      <Cuerpo>
        <p className="text-[12.5px] leading-snug text-white/55">
          La persona de la foto pasa a ser {palabra.singular === 'modelo' ? 'la modelo' : 'el avatar'} que elijas, en la misma pose y con la misma luz. El producto y la escena quedan igual.
        </p>
        <Seccion titulo={`Tus ${palabra.plural}`}>
          <div className="p-2">
            <GrillaDeEleccion
              items={conFoto?.map((m) => ({ id: String(m.id), nombre: m.nombre, imagen: fotoDe(m) })) ?? null}
              elegido={elegido}
              onElegir={setElegido}
              aspecto="3 / 4"
              vacio={`Todavía no tenés ${palabra.plural} guardados.`}
              disabled={ocupado}
            />
          </div>
        </Seccion>
      </Cuerpo>
      <Pie>
        <BotonConCreditos creditos={1} ocupado={ocupado} disabled={!modelo} onClick={() => modelo && onCambiar(modelo)}>
          {ocupado ? 'Cambiando…' : `Cambiar ${palabra.singular}`}
        </BotonConCreditos>
      </Pie>
    </>
  );
};

/** El id de la fila nueva de una edición, o el error de la respuesta. */
function idDeLaEdicion(data: ImageEditResponse): string {
  if (data.response === 'error_moderation') {
    throw new Error('La imagen no pasó el filtro de contenido. Probá con otra opción.');
  }
  const id = data.edited_image_id ?? data.imagen_id ?? data.id;
  if (data.response !== 'success' || !id) throw new Error(data.error || 'No pudimos editar la imagen. Probá de nuevo.');
  return String(id);
}

// ── El panel ────────────────────────────────────────────────────────────────────

export function EdicionEnElVisor({
  visible,
  imgRef,
  imagen,
  companyId,
  herramienta,
  onHerramienta,
  onVersionNueva,
  onMejorada,
  onTrabajo,
  onEmpieza,
  onLimite,
  onError,
  onDescargar,
  descargando = false,
}: {
  visible: boolean;
  imgRef: RefObject<HTMLImageElement>;
  imagen: ImagenEnEdicion;
  companyId: string | null;
  /** La herramienta abierta, o `null` = la lista (y el prompt de abajo, que es lo de siempre). */
  herramienta: HerramientaDeEdicion | null;
  onHerramienta: (h: HerramientaDeEdicion | null) => void;
  /** Recorte o expansión: una fila nueva, que sale de `baseId`. */
  onVersionNueva: (fila: FilaEditada, baseId: string) => void;
  /** La mejora quedó en la misma imagen. */
  onMejorada: (upscaledPath: string, signedUrl: string, baseId: string) => void;
  /** Mientras corre algo (para apagar la imagen y mostrar el brillo), con su texto. */
  onTrabajo: (texto: string | null) => void;
  /** Arrancó una herramienta que crea imagen: el id de la fila nueva, para que quien prestó
   *  el visor la siga en su grilla aunque el visor se cierre. */
  onEmpieza?: (trabajo: Promise<string | null>, info: { baseId: string; creditos: number }) => void;
  onLimite: (t: TipoDeLimite) => void;
  onError: (mensaje: string) => void;
  /** Descargar la imagen que se ve: el botón del pie de la lista, la MISMA descarga que la
   *  barra del visor (ADR-2026-10-07-descargar-en-el-panel-de-edicion). Sin él, no hay botón. */
  onDescargar?: () => void;
  descargando?: boolean;
}) {
  const [ocupado, setOcupado] = useState(false);
  /** Hay capas separadas sin guardar: volver a la lista pasa por su "Cancelar". */
  const [capasSinGuardar, setCapasSinGuardar] = useState(false);
  const [medidas, setMedidas] = useState<Dimensiones | null>(null);
  const palabra = usePalabraDeModelo();
  const { rubro } = useRubroMarcaActiva();
  /** El formato que se está mirando en Expandir (la vista previa sobre la imagen). */
  const [ratioDeVista, setRatioDeVista] = useState<RatioExpansion | null>(null);

  // Las medidas del original (Expandir y Mejorar las necesitan). `crossOrigin` igual que el
  // recorte, así comparten la misma descarga del navegador.
  useEffect(() => {
    setMedidas(null);
    if (!imagen.srcOriginal) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => img.naturalWidth > 0 && setMedidas({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = imagen.srcOriginal;
  }, [imagen.srcOriginal]);

  // Hacia dónde entra el contenido: de la derecha al abrir una herramienta, de la
  // izquierda al volver a la lista.
  const [sentido, setSentido] = useState<'adelante' | 'atras'>('adelante');
  const abrir = (h: HerramientaDeEdicion | null) => {
    setSentido(h ? 'adelante' : 'atras');
    onHerramienta(h);
  };
  /** Terminó una herramienta (recorte guardado, expansión, mejora): el panel vuelve a la
   *  lista, su primer estado (ADR-2026-10-06-al-terminar-el-panel-vuelve-a-la-lista). */
  const volverALaLista = () => abrir(null);

  /** `texto` null: sin el cartel ni el brillo sobre toda la imagen (editar una capa lo muestra sólo en ella). */
  const conGuard = async (costo: number, texto: string | null, trabajo: () => Promise<void>) => {
    if (ocupado || !companyId) return;
    if (costo > 0) {
      const saldo = await consultarSaldo(companyId);
      if (saldo.restantes !== null && saldo.restantes < costo) {
        onLimite(tipoDeLimite(saldo.plan));
        return;
      }
    }
    setOcupado(true);
    onTrabajo(texto);
    try {
      await trabajo();
    } catch (err) {
      if (err instanceof GenerateAdError && err.errorType === 'billing') {
        const saldo = await consultarSaldo(companyId);
        onLimite(tipoDeLimite(saldo.plan));
      } else {
        onError(err instanceof Error && err.message ? err.message : 'No pudimos procesar la imagen. Probá de nuevo.');
      }
    } finally {
      setOcupado(false);
      onTrabajo(null);
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {}
    }
  };

  /**
   * Una herramienta que crea imagen nueva: `crear` devuelve el id (o file_name) que dio el
   * backend; acá se encuentra la fila, se activa y se entrega. Todo eso es UNA promesa que
   * sale apenas arranca (`onEmpieza`), así la grilla de quien prestó el visor la sigue aunque
   * el visor se cierre (ADR-2026-10-06-las-ediciones-del-visor-aterrizan-en-la-grilla).
   */
  const entregar = async (baseId: string, creditos: number, crear: () => Promise<string>) => {
    if (!companyId) return;
    const trabajo = (async () => {
      const crudo = await crear();
      const fila = await buscarFila(companyId, crudo);
      if (!fila?.file_name) throw new Error('La versión se guardó, pero no pudimos cargarla. Mirá Creaciones.');
      await activarVersion(String(fila.id), companyId);
      return fila;
    })();
    onEmpieza?.(trabajo.then((f) => String(f.id)), { baseId, creditos });
    const fila = await trabajo;
    onVersionNueva(fila, baseId);
    volverALaLista();
  };

  const recortar = (area: { x: number; y: number; width: number; height: number }, formato?: string) => {
    const baseId = imagen.id;
    void conGuard(0, 'Guardando el recorte…', async () => {
      await entregar(baseId, 0, () => guardarRecorte({ imagenId: baseId, area, formato }));
    });
  };

  const expandir = (ratio: RatioExpansion) => {
    const baseId = imagen.id;
    void conGuard(CREDITOS_EXPANSION, 'Expandiendo la imagen…', async () => {
      if (!companyId) return;
      await entregar(baseId, CREDITOS_EXPANSION, async () => {
        const r = await expandirImagen({ imagenId: baseId, aspectRatio: ratio, companyId, modoUtilizado: 'expansion_magica_visor' });
        return r.id;
      });
    });
  };

  const mejorar = (escala: 2 | 4) => {
    const baseId = imagen.id;
    void conGuard(escala === 2 ? 1 : 2, 'Mejorando la resolución…', async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
      const data = await postImageUpscale({ user_id: user.id, image_id: baseId, scale: escala });
      if (data.success) {
        const path = data.upscaled_path || `${companyId}/upscaled/${imagen.fileName}`;
        const { data: firmada } = await supabase.storage.from('generadas').createSignedUrl(path, 3600);
        if (!firmada?.signedUrl) throw new Error('La imagen se mejoró, pero no pudimos cargarla.');
        setCachedSignedUrl(path, firmada.signedUrl);
        // Si mientras tanto se cambió de versión, igual se avisa: la mejora es de `baseId`.
        onMejorada(path, firmada.signedUrl, baseId);
        volverALaLista();
      } else if (data.error === 'insufficient_credits') {
        onLimite(tipoDeLimite(data.plan));
      } else {
        throw new Error(mensajeDeUpscaleFallido(data));
      }
    });
  };

  // Separar en capas: 1 crédito; las capas quedan en el lienzo del modo, sin fila nueva.
  const separar = (instruccion: string | undefined, alTerminar: (s: Separacion) => void) => {
    const baseId = imagen.id;
    void conGuard(CREDITOS_SEPARAR_EN_CAPAS, 'Separando en capas…', async () => {
      alTerminar(await separarEnCapas({ imagenId: baseId, instruccion }));
      // Separar no crea una versión (no pasa por `onVersionNueva`), así que suena acá.
      sonarListo();
    });
  };

  // Editar UNA capa con Sunburst (fondo transparente), 1 crédito; queda en el lienzo, sin fila
  // (ADR-2026-10-07-editar-una-capa-con-sunburst).
  const editarUnaCapa = (capa: CapaEnLienzo, instruccion: string, alTerminar: (nueva: CapaEnLienzo | null) => void) => {
    let entregada = false;
    void conGuard(CREDITOS_EDITAR_CAPA, null, async () => {
      if (!companyId) return;
      const nueva = await editarCapa({ capa, companyId, instruccion });
      entregada = true;
      alTerminar(nueva);
      sonarListo();
    }).finally(() => {
      // Sin saldo, error o sin empresa: la capa sale del estado "editando".
      if (!entregada) alTerminar(null);
    });
  };

  // Guardar las capas: la composición es lo que se ve, y queda como versión nueva (gratis).
  const guardarLasCapas = (datos: { capas: CapaEnLienzo[]; ancho: number; alto: number; formato?: string }) => {
    const baseId = imagen.id;
    void conGuard(0, 'Guardando la versión…', async () => {
      if (!companyId) return;
      await entregar(baseId, 0, async () =>
        guardarCapas({
          imagenId: baseId,
          companyId,
          png: await componerCapas(datos.capas, datos.ancho, datos.alto),
          formato: datos.formato,
        }),
      );
    });
  };

  // "Cambiar modelo" usa el modo `replicar`, que sólo existe en el backend nuevo; y se llama
  // como la app llama a las personas en este rubro (Modelo / Avatar).
  // "Separar en capas" también es sólo del backend nuevo (`/v1/images/layers` con Seedream).
  const herramientasVisibles = HERRAMIENTAS.filter((h) => (h.id !== 'modelo' && h.id !== 'capas') || USE_ATLAS_BACKEND).map((h) =>
    h.id === 'modelo' ? { ...h, nombre: `Cambiar ${palabra.singular}` } : h,
  );
  const actual = herramienta ? herramientasVisibles.find((x) => x.id === herramienta) : null;

  const cambiarProducto = (varianteId: string) => {
    const baseId = imagen.id;
    void conGuard(1, 'Cambiando el producto…', async () => {
      if (!companyId) return;
      await entregar(baseId, 1, async () => {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
        const data = await postImageEdit(
          {
            user_id: user.id,
            imagen_id: baseId,
            variante_id: varianteId,
            mode: 'cambio_producto',
            modo_utilizado: MODO_UTILIZADO_EDICION.cambio_producto ?? 'editar_imagen_producto',
          },
          { companyId },
        );
        return idDeLaEdicion(data);
      });
    });
  };

  // Cambiar la persona: el modo `replicar` del backend sobre la creación, con SÓLO la foto
  // del modelo (sin producto ni instrucción): reemplaza a la persona en la misma pose, luz
  // y escena, y la fila nueva hereda productos y formato (sin `aspect_ratio`).
  const cambiarModelo = (modelo: ModelV2) => {
    const baseId = imagen.id;
    void conGuard(1, `Cambiando ${palabra.singular === 'modelo' ? 'la modelo' : 'el avatar'}…`, async () => {
      if (!companyId) return;
      await entregar(baseId, 1, async () => {
        // La foto se firma recién ahora: la del listado puede estar vencida.
        const url = await firmarFotoDeModelo(modelo);
        if (!url) throw new Error(`No pudimos leer la foto de ${modelo.nombre}.`);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
        const data = await postImageEdit(
          {
            user_id: user.id,
            mode: 'replicar',
            imagen_id: baseId,
            modelo_url: url,
            rubro: rubroDelPrompt(rubro),
            modo_utilizado: 'cambiar_modelo_visor',
          },
          { companyId },
        );
        return idDeLaEdicion(data);
      });
    });
  };

  return (
    <>
    {visible && herramienta === 'expandir' && ratioDeVista && (
      <VistaPreviaDeExpansion imgRef={imgRef} src={imagen.srcVisible} ratio={ratioDeVista} ocupado={ocupado} />
    )}
    <div
      aria-hidden={!visible}
      {...((!visible ? { inert: '' } : {}) as Record<string, unknown>)}
      // Hasta el borde, con la X de cerrar del visor (40 px, a 16 px del borde) ENCIMA, en su
      // esquina: el panel va un escalón por debajo (z 28 < 30) y su encabezado le deja lugar
      // (ADR-2026-10-06-la-x-queda-delante-del-panel). El MISMO vidrio que la barra.
      className="absolute bottom-6 right-4 top-4 z-[28] flex w-[372px] flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.06] text-white backdrop-blur-xl"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateX(16px)',
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 240ms ease-out, transform 340ms cubic-bezier(0.32,0.72,0,1)',
      }}
    >
      {/* El contenido entra con un deslizamiento corto según el sentido (`key`). */}
      <div
        key={`${herramienta ?? 'lista'}:${imagen.id}`}
        className={`flex min-h-0 flex-1 flex-col ${sentido === 'adelante' ? 'panel-de-edicion-adelante' : 'panel-de-edicion-atras'}`}
      >
        {!actual ? (
          <>
            <div className="flex h-14 shrink-0 items-center pl-4 pr-16">
              <h3 className="text-[15px] font-semibold text-white/90">Herramientas de edición</h3>
            </div>
            {/* La lista: cada herramienta abre su menú. */}
            <div role="menu" aria-label="Herramientas de edición" className="flex flex-col gap-1 px-2">
              {herramientasVisibles.map(({ id, nombre, bajada, Icono }) => (
                <button
                  key={id}
                  type="button"
                  role="menuitem"
                  disabled={ocupado}
                  onClick={() => abrir(id)}
                  className="group flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-white/[0.06] disabled:opacity-40"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-white/[0.08] text-white/80">
                    <Icono className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold text-white/90">{nombre}</span>
                    <span className="mt-0.5 block truncate text-[12.5px] text-white/50">{bajada}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-white/35 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </button>
              ))}
            </div>
            {/* Descargar, abajo y al centro: con la barra convertida en prompt, la barra del
                visor (donde vive "Descargar") no está a la vista mientras se edita. */}
            {onDescargar && (
              <div className="mt-auto shrink-0 p-4">
                <button
                  type="button"
                  onClick={onDescargar}
                  disabled={ocupado || descargando}
                  className="inline-flex h-[var(--btn-h)] w-full items-center justify-center gap-2 rounded-md bg-white/10 px-5 text-[length:var(--text-body)] font-medium text-white transition-colors hover:bg-white/20 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  {descargando ? 'Descargando…' : 'Descargar'}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex h-14 shrink-0 items-center gap-1.5 pl-2 pr-16">
              <button
                type="button"
                onClick={() => abrir(null)}
                disabled={ocupado || capasSinGuardar}
                aria-label="Volver a las herramientas"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <h3 className="text-[15px] font-semibold text-white/90">{actual.nombre}</h3>
            </div>
                {herramienta === 'recortar' && (
              <ModoRecortar imagen={imagen} imgRef={imgRef} ocupado={ocupado} onGuardar={recortar} />
            )}
            {herramienta === 'expandir' && (
              <ModoExpandir medidas={medidas} ocupado={ocupado} onExpandir={expandir} onRatio={setRatioDeVista} />
            )}
            {herramienta === 'producto' && (
              <ModoCambiarProducto companyId={companyId} ocupado={ocupado} onCambiar={cambiarProducto} />
            )}
            {herramienta === 'modelo' && (
              <ModoCambiarModelo companyId={companyId} ocupado={ocupado} onCambiar={cambiarModelo} />
            )}
            {herramienta === 'mejorar' && <ModoMejorar imagen={imagen} medidas={medidas} ocupado={ocupado} onMejorar={mejorar} />}
            {herramienta === 'capas' && (
              <ModoCapas
                visible={visible}
                imgRef={imgRef}
                ocupado={ocupado}
                onSeparar={separar}
                onGuardar={guardarLasCapas}
                onEditarCapa={editarUnaCapa}
                onEditando={setCapasSinGuardar}
                onCancelar={volverALaLista}
              />
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
}

export default EdicionEnElVisor;
